import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { BooleanCell } from 'src/components/shared/DataGrid/BooleanCell';
import { BooleanFilterCell } from 'src/components/shared/DataGrid/BooleanFilterCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useIsFormDirty } from 'src/hooks/useIsFormDirty';
import { useReduxDispatch } from 'src/store';
import {
  LabelTemplateDetailDto,
  LabelTemplateDto
} from 'src/store/labelTemplates/labelTemplate';
import { mapLabelTemplateDetailToEdit } from 'src/store/labelTemplates/labelTemplate.map';
import {
  createLabelTemplate,
  deleteLabelTemplate,
  editLabelTemplate,
  fetchLabelTemplates
} from 'src/store/labelTemplates/labelTemplates.actions';
import { LabelTemplateApi } from 'src/store/labelTemplates/labelTemplates.api';
import { selectLabelTemplates } from 'src/store/labelTemplates/labelTemplates.selectors';
import { isLoadingLabelTemplates } from 'src/store/requests/requests.selectors';
import { LabelTemplateForm } from './LabelTemplateForm';

export const LabelTemplatesPage = () => {
  const [currentLabel, setCurrentLabel] = useState<LabelTemplateDto | null>(
    null
  );
  const [labelTemplate, setLabelTemplate] =
    useState<Nullable<LabelTemplateDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isFormDirty, callbackRef] = useIsFormDirty();

  const { t } = useTranslation();

  const dispatch = useReduxDispatch();

  const labelTemplates = useSelector(selectLabelTemplates);
  const loadingLabelTemplates = useSelector(isLoadingLabelTemplates);

  useEffect(() => {
    dispatch(fetchLabelTemplates());
  }, [dispatch]);

  useEffect(() => {
    if (currentLabel) {
      setShowDeleteModal(true);
    }
  }, [currentLabel]);

  const onCancel = () => {
    setCurrentLabel(null);
    setLabelTemplate(null);
    setShowDeleteModal(false);
    setShowEditModal(false);
  };

  const onCreate = () => {
    setShowEditModal(true);
  };

  const onEdit = async (label: LabelTemplateDto) => {
    const response = await LabelTemplateApi.getById(label.id);
    if (response.ok) {
      setLabelTemplate(response.data);
      setShowEditModal(true);
    }
  };

  const onDelete = (label: LabelTemplateDto) => {
    setCurrentLabel(label);
  };

  const onConfirmDelete = () => {
    if (currentLabel) {
      dispatch(deleteLabelTemplate(currentLabel.id, currentLabel));
      setCurrentLabel(null);
      setShowDeleteModal(false);
    }
  };

  const onSubmit = async (label: LabelTemplateDetailDto) => {
    const dto = mapLabelTemplateDetailToEdit(label);
    if (dto.id) {
      await dispatch(editLabelTemplate(dto.id, dto));
      onCancel();
    } else {
      await dispatch(createLabelTemplate(dto));
      onCancel();
    }
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('labelTemplate.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('labelTemplate.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('labelTemplate.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={labelTemplates}
        loading={loadingLabelTemplates}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        initialSort={[{ field: 'no', dir: 'asc' }]}
      >
        <Column
          field="no"
          width={200}
          title={t('props.number')}
          footerCell={() => (
            <GridCountCell
              count={labelTemplates?.length}
              label={t('labelTemplate.plural')}
            />
          )}
        />
        <Column field="name" title={t('props.name')} />
        <Column field="description" title={t('props.description')} />
        <Column
          width="160px"
          field="active"
          title={t('props.active')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => <BooleanFilterCell {...props} />}
        />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        header={`${labelTemplate ? t('edit') : t('create.verb')} ${t(
          'label.singular'
        )}`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <LabelTemplateForm
          labelTemplate={labelTemplate}
          onCancel={onCancel}
          onSubmit={onSubmit}
          ref={callbackRef}
        />
      </Modal>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('labelTemplate.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('labelTemplate.delete')} <q>{currentLabel?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
